import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { tournamentAPI } from '../services/api';
import ContestDisplay from '../components/ContestDisplay/ContestDisplay';
import './PublicTeeSheetView.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function PublicTeeSheetView() {
  const { tournamentId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({
    timeRequest: '',
    partnerRequest: '',
    pairingConcern: '',
    generalComment: ''
  });
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [playerWallet, setPlayerWallet] = useState(null);
  const [showPreferences, setShowPreferences] = useState(false);

  // Real-time countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadPublicTeeSheet();
  }, [tournamentId, token]);

  const loadPublicTeeSheet = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await tournamentAPI.getPublicTeeSheet(tournamentId, token);
      setData(response.data.data);
    } catch (err) {
      console.error('Error loading public tee sheet:', err);
      setError(err.response?.data?.error || 'Failed to load tee sheet');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    // If it's already in 12-hour format, return as is
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    // Convert 24-hour to 12-hour format
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${displayHour}:${minutes} ${period}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTimeRemaining = (deadline) => {
    if (!deadline) return null;
    
    const now = currentTime;
    const end = new Date(deadline);
    const difference = end - now;
    
    if (difference <= 0) return { expired: true };
    
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);
    
    // Determine urgency level for styling
    const totalHours = difference / (1000 * 60 * 60);
    let urgency = 'normal';
    if (totalHours <= 1) {
      urgency = 'critical'; // Last hour - red
    } else if (totalHours <= 24) {
      urgency = 'urgent'; // Last day - orange  
    } else if (totalHours <= 72) {
      urgency = 'warning'; // Last 3 days - yellow
    }
    
    return { days, hours, minutes, seconds, expired: false, urgency };
  };

  const renderFeedbackDeadlineBadge = () => {
    const { tournament, feedbackDeadline, feedbackGracePeriodMinutes } = data;
    
    if (!feedbackDeadline) return null;

    // Calculate effective deadline with grace period
    const effectiveDeadline = feedbackDeadline && feedbackGracePeriodMinutes 
      ? new Date(new Date(feedbackDeadline).getTime() + (feedbackGracePeriodMinutes * 60 * 1000))
      : feedbackDeadline;
      
    const timeRemaining = getTimeRemaining(feedbackDeadline);
    const effectiveTimeRemaining = getTimeRemaining(effectiveDeadline);

    if (effectiveTimeRemaining?.expired) {
      return (
        <div className="feedback-deadline-badge closed">
          🔒 Feedback Closed
        </div>
      );
    }

    if (timeRemaining?.expired && !effectiveTimeRemaining?.expired) {
      // In grace period
      return (
        <div className="feedback-deadline-badge grace">
          ⏳ Grace Period: {effectiveTimeRemaining.hours}h {effectiveTimeRemaining.minutes}m
        </div>
      );
    }

    if (timeRemaining) {
      const urgencyClass = timeRemaining.urgency;
      let timeText = '';
      
      if (timeRemaining.days > 0) {
        timeText = `${timeRemaining.days}d ${timeRemaining.hours}h`;
      } else if (timeRemaining.hours > 0) {
        timeText = `${timeRemaining.hours}h ${timeRemaining.minutes}m`;
      } else {
        timeText = `${timeRemaining.minutes}m`;
        if (timeRemaining.urgency === 'critical') {
          timeText += ` ${timeRemaining.seconds}s`;
        }
      }

      return (
        <div className={`feedback-deadline-badge ${urgencyClass}`}>
          {timeRemaining.urgency === 'critical' && '⚠️ '}
          {timeRemaining.urgency === 'urgent' && '🔥 '}
          {timeRemaining.urgency === 'warning' && '⏰ '}
          {timeText} remaining
        </div>
      );
    }

    return null;
  };

  const renderMemberProfile = (authData) => {
    // Check various possible locations for member data
    const memberData = authData.memberData || authData.member || authData.playerData || authData.player || authData;
    
    if (!memberData || !memberData.defaultPreferences) {
      return (
        <div className="no-member-profile">
          <p>No member profile data available.</p>
        </div>
      );
    }

    const preferences = memberData.defaultPreferences;
    const profile = [];

    // Time Slot Preference
    if (preferences.timeSlotPreference && preferences.timeSlotPreference !== 'no-preference') {
      profile.push({
        type: 'Time Preference',
        content: preferences.timeSlotPreference,
        icon: '🕐'
      });
    }

    // Playing Partner Preferences
    if (preferences.playingPartnerPreferences && preferences.playingPartnerPreferences.length > 0) {
      const partners = preferences.playingPartnerPreferences
        .map(p => `${p.displayName || p.name} (${p.relationship})`)
        .join(', ');
      profile.push({
        type: 'Preferred Partners',
        content: partners,
        icon: '🤝'
      });
    }

    // Avoid Partners
    if (preferences.avoidPartners && preferences.avoidPartners.length > 0) {
      const avoidPartners = preferences.avoidPartners
        .map(p => p.reason ? `${p.name} (${p.reason})` : p.name)
        .join(', ');
      profile.push({
        type: 'Avoid Partners',
        content: avoidPartners,
        icon: '🚫'
      });
    }

    // Ride or Walk Preference
    if (preferences.rideOrWalkPreference && preferences.rideOrWalkPreference !== 'no-preference') {
      const rideWalkText = preferences.rideOrWalkPreference === 'ride' ? 'Ride (Golf Cart)' : 'Walk';
      profile.push({
        type: 'Transportation',
        content: rideWalkText,
        icon: preferences.rideOrWalkPreference === 'ride' ? '🚗' : '🚶'
      });
    }

    return (
      <div>
        {/* Wallet Balance - Always visible */}
        {playerWallet && (
          <div className="profile-item wallet-balance-item">
            <div className="profile-type">
              <span className="profile-icon">💰</span>
              Wallet Balance
            </div>
            <p className="profile-content wallet-balance-amount">
              {formatCurrency(playerWallet.balance)}
            </p>
          </div>
        )}

        {/* Preferences Toggle */}
        {profile.length > 0 && (
          <div className="preferences-section">
            <button 
              className="view-preferences-btn"
              onClick={() => setShowPreferences(!showPreferences)}
            >
              {showPreferences ? '▼' : '▶'} View preferences
            </button>
            
            {showPreferences && (
              <div className="member-profile-list">
                {profile.map((item, index) => (
                  <div key={index} className="profile-item">
                    <div className="profile-type">
                      <span className="profile-icon">{item.icon}</span>
                      {item.type}
                    </div>
                    <p className="profile-content">{item.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {profile.length === 0 && !showPreferences && (
          <button 
            className="view-preferences-btn"
            onClick={() => setShowPreferences(!showPreferences)}
          >
            {showPreferences ? '▼' : '▶'} View preferences
          </button>
        )}
        
        {profile.length === 0 && showPreferences && (
          <div className="preferences-section">
            <button 
              className="view-preferences-btn"
              onClick={() => setShowPreferences(!showPreferences)}
            >
              ▼ View preferences
            </button>
            <div className="no-profile-preferences">
              <p>No specific preferences set in your member profile.</p>
            </div>
          </div>
        )}
        
        {/* Debug info for development */}
        <details style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#666' }}>
          <summary>Debug: Available member preference fields ({Object.keys(preferences || {}).length} fields)</summary>
          <pre style={{ maxHeight: '200px', overflow: 'auto', background: '#f5f5f5', padding: '0.5rem', marginTop: '0.5rem' }}>
            {JSON.stringify(preferences, null, 2)}
          </pre>
        </details>
      </div>
    );
  };

  const renderOriginalComments = (authData) => {
    const comments = [];
    
    // Check various possible locations for player data
    const playerData = authData.playerData || authData.player || authData;
    
    // Original pairing requests (main field from roster)
    if (playerData.originalPairingRequest && playerData.originalPairingRequest.trim()) {
      comments.push({
        type: 'Original Request',
        content: playerData.originalPairingRequest
      });
    }
    
    // Raw partner request (alternative field)
    if (playerData.rawPartnerRequest && playerData.rawPartnerRequest.trim()) {
      comments.push({
        type: 'Partner Request (Raw)',
        content: playerData.rawPartnerRequest
      });
    }
    
    // Parsed preferences
    if (playerData.parsedTimePreference && playerData.parsedTimePreference.trim()) {
      comments.push({
        type: 'Time Preference',
        content: playerData.parsedTimePreference
      });
    }
    
    if (playerData.parsedPartnerPreference && playerData.parsedPartnerPreference.trim()) {
      comments.push({
        type: 'Partner Preference',
        content: playerData.parsedPartnerPreference
      });
    }
    
    if (playerData.parsedPairingPreference && playerData.parsedPairingPreference.trim()) {
      comments.push({
        type: 'Pairing Preference',
        content: playerData.parsedPairingPreference
      });
    }
    
    // Time preferences - check multiple possible field names
    if (playerData.timeRequest || playerData.timePreference || playerData.time_request) {
      comments.push({
        type: 'Time Request',
        content: playerData.timeRequest || playerData.timePreference || playerData.time_request
      });
    }
    
    // Partner requests - check multiple possible field names
    if (playerData.partnerRequest || playerData.partnerPreference || playerData.partner_request) {
      comments.push({
        type: 'Partner Request', 
        content: playerData.partnerRequest || playerData.partnerPreference || playerData.partner_request
      });
    }
    
    // General comments - check multiple possible field names
    if (playerData.comments || playerData.generalComments || playerData.notes) {
      comments.push({
        type: 'General Comments',
        content: playerData.comments || playerData.generalComments || playerData.notes
      });
    }
    
    // Special requests or notes
    if (playerData.specialRequests || playerData.special_requests) {
      comments.push({
        type: 'Special Requests',
        content: playerData.specialRequests || playerData.special_requests
      });
    }

    // Always show debug info in development or when no comments found
    return (
      <div>
        {comments.length > 0 && (
          <div className="original-comments-list">
            {comments.map((comment, index) => (
              <div key={index} className="original-comment-item">
                <div className="comment-type">{comment.type}</div>
                <p className="comment-content">{comment.content}</p>
              </div>
            ))}
          </div>
        )}
        
        {comments.length === 0 && (
          <div className="no-original-comments">
            <p>No original requests found in your roster data.</p>
          </div>
        )}
        
        {/* Always show debug info for testing */}
        <details style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#666' }}>
          <summary>Debug: Available player fields ({Object.keys(playerData || {}).length} fields)</summary>
          <pre style={{ maxHeight: '200px', overflow: 'auto', background: '#f5f5f5', padding: '0.5rem', marginTop: '0.5rem' }}>
            {JSON.stringify(playerData, null, 2)}
          </pre>
        </details>
      </div>
    );
  };

  const handleFeedbackChange = (field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitFeedback = async () => {
    try {
      setFeedbackSubmitting(true);
      
      // Check if feedback deadline has passed (including grace period)
      if (effectiveDeadline) {
        const now = new Date();
        
        if (now > new Date(effectiveDeadline)) {
          const gracePeriodText = feedbackGracePeriodMinutes > 0 
            ? ` (including ${feedbackGracePeriodMinutes} minute grace period)`
            : '';
          alert(`The feedback deadline has passed${gracePeriodText}. You can no longer submit feedback for this tournament.`);
          setFeedbackSubmitting(false);
          return;
        }
      }
      
      // Build comments array from form data
      const comments = [];
      
      if (feedbackForm.timeRequest.trim()) {
        comments.push({
          type: 'time',
          content: feedbackForm.timeRequest.trim()
        });
      }
      
      if (feedbackForm.partnerRequest.trim()) {
        comments.push({
          type: 'partner',
          content: feedbackForm.partnerRequest.trim()
        });
      }
      
      if (feedbackForm.pairingConcern.trim()) {
        comments.push({
          type: 'pairing',
          content: feedbackForm.pairingConcern.trim()
        });
      }
      
      if (feedbackForm.generalComment.trim()) {
        comments.push({
          type: 'general',
          content: feedbackForm.generalComment.trim()
        });
      }
      
      if (comments.length === 0) {
        alert('Please provide at least one comment before submitting.');
        return;
      }
      
      await tournamentAPI.submitFeedback(tournamentId, comments, token);
      
      setFeedbackSuccess(true);
      setFeedbackForm({
        timeRequest: '',
        partnerRequest: '',
        pairingConcern: '',
        generalComment: ''
      });
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(error.response?.data?.error || 'Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-tee-sheet-view loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading tee sheet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-tee-sheet-view error">
        <div className="error-message">
          <h2>Oops!</h2>
          <p>{error}</p>
          <button onClick={loadPublicTeeSheet} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="public-tee-sheet-view error">
        <div className="error-message">
          <h2>No Data</h2>
          <p>No tee sheet data available.</p>
        </div>
      </div>
    );
  }

  const { tournament, teeSheet, authentication, feedbackDeadline, feedbackGracePeriodMinutes } = data;
  
  // Calculate effective deadline with grace period
  const effectiveDeadline = feedbackDeadline && feedbackGracePeriodMinutes 
    ? new Date(new Date(feedbackDeadline).getTime() + (feedbackGracePeriodMinutes * 60 * 1000))
    : feedbackDeadline;
    
  const timeRemaining = getTimeRemaining(feedbackDeadline);
  const effectiveTimeRemaining = getTimeRemaining(effectiveDeadline);

  return (
    <div className="public-tee-sheet-view">
      {/* Header */}
      <header className="public-header">
        <div className="tournament-info">
          <h1>{tournament.name}</h1>
          <div className="tournament-details">
            <span className="tournament-date">{formatDate(tournament.date)}</span>
            {tournament.course && (
              <span className="tournament-course">• {tournament.course}</span>
            )}
            <span className="tournament-version">• Version {tournament.version}</span>
            <span className="tee-sheet-summary">• {teeSheet.totalGroups} groups • {teeSheet.totalPlayers} players</span>
          </div>
        </div>

        {/* Authentication Status - Only show for invalid/readonly */}
        {!authentication.isValidToken && token ? (
          <div className="auth-status invalid">
            <p>Invalid access token. Showing read-only view.</p>
          </div>
        ) : !authentication.isValidToken && !token ? (
          <div className="auth-status readonly">
            <p>Read-only view • Players can access personalized view with their GHIN number</p>
          </div>
        ) : null}
      </header>

      {/* Tee Sheet Display */}
      <main className="tee-sheet-content">
        <div className="tee-sheet-main">
          <div className="tee-sheet-table-container">
            <table className="tee-sheet-table">
              <thead>
                <tr>
                  <th className="tee-time-header">Tee Time</th>
                  <th className="player-header">Player 1</th>
                  <th className="player-header">Player 2</th>
                  <th className="player-header">Player 3</th>
                  <th className="player-header">Player 4</th>
                </tr>
              </thead>
              <tbody>
                {teeSheet.groups.map((group, index) => {
                  const hasAuthenticatedPlayer = group.players.some(p => p.isAuthenticatedPlayer);
                  
                  return (
                    <tr 
                      key={group.groupNumber} 
                      className={`group-row ${hasAuthenticatedPlayer ? 'authenticated-group' : ''}`}
                    >
                      <td className="tee-time-cell">
                        <div className="tee-time-display">{formatTime(group.teeTime)}</div>
                      </td>
                      {[0, 1, 2, 3].map((playerIndex) => {
                        const player = group.players[playerIndex];
                        return (
                          <td key={playerIndex} className="player-cell">
                            {player ? (
                              <div className={`player-info ${player.isAuthenticatedPlayer ? 'authenticated-player' : ''}`}>
                                <div className="player-name">
                                  {player.name}
                                  {player.isAuthenticatedPlayer && <span className="you-badge">YOU</span>}
                                </div>
                                <div className="player-handicap">HI: {player.handicapIndex}</div>
                              </div>
                            ) : (
                              <div className="empty-player">—</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Feedback Gutter */}
        {authentication.isValidToken && (
          <div className="feedback-gutter">
            {/* Player Welcome Section */}
            <div className="player-welcome-section">
              <div className="player-welcome">
                <h3>Welcome, {authentication.playerName}!</h3>
                {/* {authentication.playerGroup && (
                  <p>
                    You're in Group {authentication.playerGroup.groupNumber} 
                    • Tee Time: {formatTime(authentication.playerGroup.teeTime)}
                  </p>
                )} */}
              </div>
              
              {/* Member Profile integrated into welcome */}
              <div className="member-profile-content">
                {renderMemberProfile(authentication)}
              </div>
            </div>

            {/* Contest Display */}
            <ContestDisplay 
              tournamentId={tournamentId}
              playerGhin={authentication?.playerGhin || token}
              showPlayerActions={authentication?.isValidToken || !!token}
              onWalletLoaded={setPlayerWallet}
            />

            {/* Original Player Comments Section */}
            <div className="original-comments-section">
              <h3>
                <span className="section-icon">📝</span>
                Your Original Requests
              </h3>
              {renderOriginalComments(authentication)}
            </div>

            {/* Feedback Section */}
            {!effectiveTimeRemaining?.expired && (
              <div className="feedback-section">
                {feedbackSuccess ? (
                  <div className="feedback-success">
                    <h3>✅ Thank You!</h3>
                    <p>Your feedback has been submitted successfully. The tournament director will review your comments.</p>
                    <button 
                      onClick={() => setFeedbackSuccess(false)}
                      className="submit-more-button"
                    >
                      Submit More Feedback
                    </button>
                  </div>
                ) : (
                  <div className="feedback-form">
                    <div className="feedback-header">
                      <h3>Feedback</h3> {renderFeedbackDeadlineBadge()}
                    </div>
                  
  
                    
                    <div className="feedback-categories">
                      <div className="feedback-category">
                        <h4>Tee Time Request</h4>
                        <p>Request an earlier or later tee time if needed</p>
                        <textarea
                          value={feedbackForm.timeRequest}
                          onChange={(e) => handleFeedbackChange('timeRequest', e.target.value)}
                          placeholder="Example: I would prefer an earlier tee time due to other commitments..."
                          maxLength={1000}
                          rows={3}
                        />
                      </div>
                      
                      <div className="feedback-category">
                        <h4>Partner Request</h4>
                        <p>Request to play with or avoid specific players</p>
                        <textarea
                          value={feedbackForm.partnerRequest}
                          onChange={(e) => handleFeedbackChange('partnerRequest', e.target.value)}
                          placeholder="Example: I would like to be paired with John Smith, or I need to avoid slow players due to time constraints..."
                          maxLength={1000}
                          rows={3}
                        />
                      </div>
                      
                      <div className="feedback-category">
                        <h4>Group Concerns</h4>
                        <p>Issues with your current group assignment</p>
                        <textarea
                          value={feedbackForm.pairingConcern}
                          onChange={(e) => handleFeedbackChange('pairingConcern', e.target.value)}
                          placeholder="Example: I have a conflict with someone in my group, or the pace of play might not match..."
                          maxLength={1000}
                          rows={3}
                        />
                      </div>
                      
                      <div className="feedback-category">
                        <h4>General Comments</h4>
                        <p>Any other feedback about the tournament</p>
                        <textarea
                          value={feedbackForm.generalComment}
                          onChange={(e) => handleFeedbackChange('generalComment', e.target.value)}
                          placeholder="Example: Overall thoughts about the tournament setup, suggestions for improvement..."
                          maxLength={1000}
                          rows={3}
                        />
                      </div>
                    </div>
                    
                    <div className="feedback-actions">
                      <button 
                        onClick={submitFeedback}
                        disabled={feedbackSubmitting}
                        className="submit-feedback-button"
                      >
                        {feedbackSubmitting ? 'Submitting...' : 'Submit Feedback'}
                      </button>
                      <p className="feedback-note">
                        💡 All fields are optional. Only submit feedback for areas you have concerns about.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="public-footer">
        <p>Pards Tee Maker • Tournament Management System</p>
        {!authentication.isValidToken && (
          <p className="access-note">
            Have your GHIN number? Add ?token=YOUR_GHIN to the URL for personalized access
          </p>
        )}
      </footer>
    </div>
  );
}

export default PublicTeeSheetView;